import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logger } from "../../utils/logger";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, MoreVertical, Bot, Trash2,
  XCircle, Inbox, ArrowRight, Package, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import DateRangeFilter from "./DateRangeFilter";
import InvoiceDocumentPreview from "./InvoiceDocumentPreview";
import { useInvoicePreview } from "@/contexts/InvoicePreviewContext";
import { usePageContainer } from "@/hooks/usePageContainer";
import { formatDueDate, formatAmount as fmtAmount } from "@/lib/formatters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Opcje sortowania listy Faktur (mapowanie value → backend sort_by + sort_dir).
const SORT_OPTIONS = [
  { value: "newest",       label: "Najnowsze najpierw", sort_by: "issue_date",   sort_dir: "desc" },
  { value: "oldest",       label: "Najstarsze najpierw", sort_by: "issue_date",  sort_dir: "asc"  },
  { value: "amount_desc",  label: "Kwota malejąco",     sort_by: "gross_amount", sort_dir: "desc" },
  { value: "amount_asc",   label: "Kwota rosnąco",      sort_by: "gross_amount", sort_dir: "asc"  },
  { value: "vendor_az",    label: "Kontrahent A-Z",     sort_by: "seller_name",  sort_dir: "asc"  },
  { value: "vendor_za",    label: "Kontrahent Z-A",     sort_by: "seller_name",  sort_dir: "desc" },
];

/**
 * InvoicesStageBadge — odpowiednik WyciagiStageBadge z BankStatementsPage.
 *
 * Dynamiczny badge wskazujący następny krok w workflow Faktur. Wylicza
 * etap na podstawie istniejących endpointów (BEZ zmian backendu):
 *   - prop `hasInvoices` przekazany z rodzica (już znany, lista faktur).
 *   - GET /api/ai/agents/invoice/vendors → czy istnieje przynajmniej
 *     1 profil kontrahenta (profile_status ∈ {analyzed, needs_review}).
 *
 * Mapowanie:
 *   Brak faktur                       → "Krok 4: Zaimportuj faktury"
 *   Faktury są, brak profili          → "Krok 4: Uruchom Agenta Faktur"
 *   Przynajmniej 1 profil             → "Krok 4: Profile dostawców gotowe"
 *
 * `reloadKey` pozwala wymusić odświeżenie po zmianach na liście.
 */
const InvoicesStageBadge = ({ hasInvoices, reloadKey }) => {
  const [label, setLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasInvoices) {
        if (!cancelled) setLabel("Krok 4: Zaimportuj faktury");
        return;
      }
      try {
        const res = await axios.get(
          `${API}/ai/agents/invoice/vendors`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const items = res.data?.vendors || [];
        const hasAnyProfile = items.some(
          (v) => v.profile_status === "analyzed" || v.profile_status === "needs_review"
        );
        setLabel(
          hasAnyProfile
            ? "Krok 4: Profile dostawców gotowe"
            : "Krok 4: Uruchom Agenta Faktur"
        );
      } catch {
        // Fallback — gdy endpoint chwilowo niedostępny, pokaż neutralny stan.
        if (!cancelled) setLabel("Krok 4: Uruchom Agenta Faktur");
      }
    })();
    return () => { cancelled = true; };
  }, [hasInvoices, reloadKey]);

  if (!label) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
      data-testid="invoices-stage-badge"
    >
      {label}
    </span>
  );
};

/**
 * InvoiceAgentCta — kafel/komunikat na dole strony Faktury.
 *
 * Wzorzec 1:1 z `BankAgentCta` w `BankStatementsPage.jsx`. Nie robi własnego
 * fetcha — korzysta z propa `hasInvoices`, który strona już zna.
 *
 *  - Gdy brak faktur: subtelny komunikat informacyjny + disabled CTA.
 *  - Gdy są faktury: pełnoprawny kafel z aktywnym przyciskiem.
 */
const InvoiceAgentCta = ({ hasInvoices }) => {
  const navigate = useNavigate();

  if (!hasInvoices) {
    return (
      <Card
        className="mt-6 border-dashed border-gray-300 bg-gray-50"
        data-testid="invoice-agent-cta-empty"
      >
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-1.5 bg-white rounded-md border border-gray-200">
            <Bot className="w-4 h-4 text-gray-500" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-gray-800">Najpierw zaimportuj faktury z KSeF.</p>
            <p className="text-gray-600 mt-0.5 text-xs">
              Agent Faktur analizuje zaimportowane faktury. Po zaimportowaniu
              pierwszych dokumentów pojawi się możliwość uruchomienia analizy AI.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="mt-6 border-indigo-200 bg-indigo-50/40"
      data-testid="invoice-agent-cta"
    >
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="p-2 bg-white rounded-md border border-indigo-200 shrink-0">
          <Bot className="w-5 h-5 text-indigo-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">Analiza AI faktur</p>
          <p className="text-xs text-gray-600 mt-0.5">
            Utwórz profile dostawców kontrahentów na podstawie historii faktur KSeF.
          </p>
        </div>
        <Button
          onClick={() => navigate("/faktury/agent")}
          data-testid="invoice-agent-open-btn"
          className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
        >
          Otwórz Agenta Faktur
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
};

const InvoicesPage = () => {
  // Pakiet 1: lista faktur z desktopową tabelą 7-kol — szerszy kontener.
  usePageContainer("wide");
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filtr zakresu dat (Agent 1) — preset + obliczone date_from/date_to (YYYY-MM-DD)
  const [dateRange, setDateRange] = useState({ preset: "30d", date_from: "", date_to: "" });
  // Sortowanie listy (default: newest = issue_date desc)
  const [sortValue, setSortValue] = useState("newest");

  // 2026-07 v4: Widok listy (P0 UX po lifecycle mark-as-paid).
  // "to_pay" = otwarte, "paid" = zapłacone, "all" = wszystkie.
  // Default: to_pay — user pierwsze co chce widzieć to „co mam do zapłaty".
  const [view, setView] = useState("to_pay");

  // Wskaźnik „Zapłacone w tym miesiącu" (agregacja backendowa).
  const [monthlyPaid, setMonthlyPaid] = useState(null);  // {month, count, total_amount, currency}
  
  // KSeF Sync state — USUNIĘTE (2026-06-02): główny kanał pobierania to KSeF Inbox.
  // Modal „Pobierz z KSeF" + handleKsefSync usunięte. Przycisk header linkuje do /ksef-inbox.

  // Exclude invoice confirm dialog state (zamiast window.confirm — działa w iframe)
  const [excludeDialogInvoiceId, setExcludeDialogInvoiceId] = useState(null);
  const [excluding, setExcluding] = useState(false);

  // Delete-from-Inbox dialog state (usuwa rekord z ksef_invoices po ksef_number)
  const [deleteDialogInvoice, setDeleteDialogInvoice] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Usuń fakturę z modułu Faktury (kolekcja `invoices`, nie ksef_invoices)
  const [deleteInvoiceDialog, setDeleteInvoiceDialog] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);

  // Round 21: JEDYNY globalny mechanizm — context, jeden Dialog w MainLayout.
  const { openInvoice } = useInvoicePreview();

  const fetchInvoices = useCallback(async () => {
    try {
      let params = new URLSearchParams();
      if (search) params.append('search', search);
      if (view) params.append('view', view);
      if (dateRange.date_from) params.append('date_from', dateRange.date_from);
      if (dateRange.date_to) params.append('date_to', dateRange.date_to);
      const sortOpt = SORT_OPTIONS.find(o => o.value === sortValue) || SORT_OPTIONS[0];
      params.append('sort_by', sortOpt.sort_by);
      params.append('sort_dir', sortOpt.sort_dir);

      const response = await axios.get(`${API}/invoices?${params.toString()}`, { withCredentials: true });
      setInvoices(response.data.invoices);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching invoices:', error);
      toast.error('Błąd pobierania faktur');
      setLoading(false);
    }
  }, [search, view, dateRange, sortValue]);

  // Wskaźnik „Zapłacone w tym miesiącu" — pobierany raz przy mount,
  // odświeżany przy `pax:packages:refresh` (emitowany po mark/unmark-as-paid).
  const fetchMonthlyPaid = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/monthly-paid-stats`, { withCredentials: true });
      setMonthlyPaid(res.data);
    } catch (_e) {
      setMonthlyPaid(null);
    }
  }, []);

  useEffect(() => {
    fetchMonthlyPaid();
    const onRefresh = () => fetchMonthlyPaid();
    window.addEventListener("pax:packages:refresh", onRefresh);
    return () => window.removeEventListener("pax:packages:refresh", onRefresh);
  }, [fetchMonthlyPaid]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // 2026-02 round 8: po zmianie preferowanej metody płatności kontrahenta
  // (`PUT /api/vendors/{nip}/payment-method`) w innym module (Agent Płatności),
  // backend przelicza `workflow_status`. Musimy odświeżyć listę faktur, żeby
  // pole `vendor_workflow_status` + filtr `view=to_pay` wróciły spójne
  // z nowym stanem. Event `pax:packages:refresh` jest emitowany przez
  // `PaymentPackagesPage.handleMethodChanged`. Bez tego user musiałby ręcznie
  // odświeżyć stronę.
  useEffect(() => {
    const onRefresh = () => fetchInvoices();
    window.addEventListener("pax:packages:refresh", onRefresh);
    return () => window.removeEventListener("pax:packages:refresh", onRefresh);
  }, [fetchInvoices]);

  // === Podgląd faktury (ujednolicony z KSeF Inbox) ===
  // Builduje obiekt zgodny z kontraktem GET /api/ksef/inbox/{ksef_number}
  // z metadanych invoice (fallback gdy brak ksef_number lub gdy bufor został wyczyszczony).
  const buildFallbackPreview = useCallback((inv) => ({
    invoice_number: inv.invoice_number || "",
    ksef_number: inv.ksef_number || "",
    invoice_date: inv.issue_date || "",
    seller_name: inv.seller_name || "",
    seller_nip: inv.seller_nip || "",
    seller_address: "",
    seller_email: "",
    seller_phone: "",
    buyer_name: inv.buyer_name || "",
    buyer_nip: inv.buyer_nip || "",
    buyer_address: "",
    currency: inv.currency || "PLN",
    gross_amount: inv.gross_amount || 0,
    net_amount: inv.net_amount || 0,
    vat_amount: inv.vat_amount || 0,
    invoice_type: "",
    due_date: inv.due_date || "",
    payment_method: "",
    bank_account: inv.bank_account || "",
    service_date: "",
    items: [],
    metadata_only: true,
    xml_fetch_error: "",
  }), []);

  const openPreview = (inv) => openInvoice(inv);

  const handleExclude = (invoiceId) => {
    // Otwieramy dialog zamiast window.confirm — confirm jest blokowany
    // w embedded iframe Preview aplikacji Emergent.
    setExcludeDialogInvoiceId(invoiceId);
  };

  // === Ręczne oznaczenie faktury jako zapłaconej (menu ⋮) ===
  // Toggle: unpaid → paid → unpaid. Zapisuje datę + źródło zmiany
  // (`manual_user_invoice`). Faktura zapłacona nie wchodzi do analizy
  // Agenta Płatności ani do tworzenia paczek (filtr `_match_open_invoices`).
  const handleTogglePaid = async (invoice) => {
    const isCurrentlyPaid = String(invoice.status || "").toLowerCase() === "paid";
    try {
      const res = await axios.post(
        `${API}/invoices/${invoice.invoice_id}/toggle-paid`,
        {},
        { withCredentials: true },
      );
      const isPaid = !!res.data?.is_paid;
      const removedFromPackages = res.data?.removed_from_packages || [];
      const removedCount = removedFromPackages.length;
      // 2026-02 P2: gdy faktura była w paczce draft/proposal — została stamtąd
      // usunięta automatycznie (rozwiązanie „phantom in_batch"). User dostaje
      // dodatkowy toast informacyjny o czyszczeniu paczek.
      toast.success(
        isPaid
          ? "Faktura oznaczona jako zapłacona."
          : "Cofnięto oznaczenie „Zapłacona”.",
        {
          description: isPaid
            ? (removedCount > 0
                ? `Nie trafi do Agenta Płatności. Usunięto również z ${removedCount} paczki(-ek) niewyeksportowanej(-ych).`
                : "Nie trafi do Agenta Płatności ani do nowych paczek.")
            : "Faktura wraca do listy otwartych. Nie została automatycznie ponownie dodana do paczki.",
        },
      );
      fetchInvoices();
    } catch (error) {
      logger.error("Error toggling invoice paid:", error);
      toast.error(
        isCurrentlyPaid
          ? "Nie udało się cofnąć oznaczenia „Zapłacona”."
          : "Nie udało się oznaczyć faktury jako zapłaconej.",
      );
    }
  };

  // === Ręczne dodanie do paczki płatności (menu trzech kropek) ===
  // Idempotentne: backend znajduje najnowszą draft-paczkę usera lub tworzy
  // nową. Duplikat faktury nie powstaje — zwracamy `already_in_package`.
  // Nie przenosimy usera nigdzie — po prostu toast + zamknięcie menu.
  const handleAddToPackage = async (invoice) => {
    try {
      const res = await axios.post(
        `${API}/payment-packages/add-to-draft`,
        { invoice_id: invoice.invoice_id },
        { withCredentials: true },
      );
      const status = res.data?.status;
      if (status === "already_in_package") {
        toast.info("Faktura jest już w paczce");
      } else {
        toast.success("Dodano do paczki", {
          description: res.data?.package_name || undefined,
        });
        // Odśwież listę — status faktury zmienił się na `in_batch`.
        fetchInvoices();
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "already_paid_in_ksef_xml") {
        toast.warning("Faktura opłacona w KSeF", {
          description: "Nie można dodać do paczki płatności.",
        });
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Nie udało się dodać faktury do paczki.",
        );
      }
    }
  };

  const confirmExclude = async () => {
    if (!excludeDialogInvoiceId) return;
    setExcluding(true);
    try {
      await axios.post(`${API}/invoices/${excludeDialogInvoiceId}/exclude`, {}, { withCredentials: true });
      toast.success('Faktura wykluczona');
      setExcludeDialogInvoiceId(null);
      fetchInvoices();
    } catch (error) {
      logger.error('Error excluding invoice:', error);
      toast.error('Błąd wykluczania faktury');
    } finally {
      setExcluding(false);
    }
  };

  // === Usuwanie z KSeF Inbox (kolekcja ksef_invoices) ===
  // Wymaga ksef_number na fakturze. Nie usuwa rekordu z `invoices`.
  const handleDeleteFromInbox = (invoice) => {
    if (!invoice?.ksef_number) {
      toast.error('Faktura nie ma powiązania z KSeF (brak ksef_number)');
      return;
    }
    setDeleteDialogInvoice(invoice);
  };

  const confirmDeleteFromInbox = async () => {
    if (!deleteDialogInvoice?.ksef_number) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/ksef/inbox/${encodeURIComponent(deleteDialogInvoice.ksef_number)}`,
        { withCredentials: true }
      );
      toast.success('Usunięto fakturę z Inboxa');
      setDeleteDialogInvoice(null);
      // Lista PAX z `invoices` nie zmienia się — rekord tam zostaje (był imported).
    } catch (error) {
      logger.error('Error deleting from inbox:', error);
      const msg = error.response?.data?.detail || 'Błąd usuwania faktury z Inboxa';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // === Usuwanie faktury z modułu Faktury (kolekcja `invoices`) ===
  // NIE dotyka `ksef_invoices` / KSeF Inbox — tylko czyści listę roboczą Faktury.
  const handleDeleteInvoice = (invoice) => {
    setDeleteInvoiceDialog(invoice);
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteInvoiceDialog?.invoice_id) return;
    setDeletingInvoice(true);
    try {
      await axios.delete(
        `${API}/invoices/${encodeURIComponent(deleteInvoiceDialog.invoice_id)}`,
        { withCredentials: true }
      );
      toast.success('Faktura usunięta');
      setDeleteInvoiceDialog(null);
      fetchInvoices();
    } catch (error) {
      logger.error('Error deleting invoice:', error);
      const msg = error.response?.data?.detail || 'Błąd usuwania faktury';
      toast.error(msg);
    } finally {
      setDeletingInvoice(false);
    }
  };

  // === Stary single-vendor flow AI usunięty (2026-02-14).
  // Analiza AI dostępna wyłącznie przez InvoiceAgentCta → /faktury/agent.
  // Rollback: docs/INVOICE_AGENT_UX_ALIGNMENT_WITH_BANK_AGENT_FIX.md

  // Pakiet 1 (2026-02-24): lokalne formatAmount/formatDate zastąpione centralnymi
  // z `lib/formatters`. `formatDueDate` daje fallback „Brak terminu" zamiast „—"
  // dla brakującego terminu płatności (znaczące UX polepszenie).
  const formatAmount = fmtAmount;
  const formatDate = formatDueDate;

  // 2026-02: Status "Czeka" (waiting) usunięty całkowicie z backendu — badge
  // nic nie wnosił dla usera, a zajmował miejsce obok numeru faktury.
  // Świeże faktury (KSeF/manual) NIE mają już pola `status` (brak decyzji =
  // brak statusu). Filtr `_match_open_invoices` akceptuje brak statusu.
  // Layout `flex items-center gap-2 flex-wrap` sam się dopasuje.
  //
  // 2026-02: dla faktur zapłaconych rozróżniamy w tooltipie:
  //   - `paid_manual` → ręcznie oznaczona (menu ⋮)
  //   - `paid_by_export` → oznaczona przez eksport bankowy (Archiwum → mark-as-paid)
  //   Source of truth: computed pole `payment_status` z backendu
  //   (`services/invoice_payment_status.compute_payment_status`).
  const getStatusBadge = (statusOrInvoice) => {
    // Backward compat: przyjmujemy zarówno sam string status jak i cały invoice.
    const isInvoiceObj = statusOrInvoice && typeof statusOrInvoice === "object";
    const status = isInvoiceObj ? statusOrInvoice.status : statusOrInvoice;
    const paymentStatus = isInvoiceObj ? statusOrInvoice.payment_status : null;
    const badges = {
      // brak statusu (`undefined`/`null`) / unknown → nic nie renderujemy —
      // po 2026-02 to kanoniczny stan otwartej faktury.
      // waiting: usunięty świadomie (2026-02) — nie ma już w bazie.
      to_decide: { class: 'bg-blue-100 text-blue-800', label: 'Do decyzji' },
      urgent: { class: 'bg-red-100 text-red-800', label: 'Pilne' },
      in_batch: { class: 'bg-purple-100 text-purple-800', label: 'W paczce' },
      paid: { class: 'bg-green-100 text-green-800', label: 'Zapłacona' },
      partial: { class: 'bg-yellow-100 text-yellow-800', label: 'Częściowo' },
      excluded: { class: 'bg-gray-100 text-gray-400', label: 'Wykluczona' }
    };
    const badge = badges[status];
    if (!badge) return null;  // brak statusu / unknown → nic nie renderujemy
    // Dla `paid` — tooltip z rozróżnieniem źródła.
    const title = status === "paid"
      ? (paymentStatus === "paid_manual"
          ? "Zapłacona ręcznie (menu ⋮ na fakturze)"
          : paymentStatus === "paid_by_export"
            ? "Zapłacona przez eksport bankowy (Archiwum → Oznacz jako zapłacone)"
            : "Zapłacona")
      : undefined;
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.class}`}
        title={title}
      >
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page">
      {/* Header — minimalistyczny, parity z BankStatementsPage (P3A).
          - H1 zwięzłe ("Faktury", nie "Faktury z KSeF") — spójne z Sidebar entry.
          - Subtitle usunięty (redundant).
          - Stage badge w jednym flex row (mobile-friendly).
          - KSeF Inbox CTA przeniesione obok H1 — `flex-wrap` zapewnia czytelność. */}
      <header className="page-header flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Faktury</h1>
        <InvoicesStageBadge hasInvoices={invoices.length > 0} />
        <div className="ml-auto">
          <Link to="/ksef-inbox" data-testid="ksef-inbox-link">
            <Button className="btn-primary btn-responsive" data-testid="ksef-inbox-btn">
              <Inbox className="w-4 h-4 mr-2" />
              KSeF Inbox
            </Button>
          </Link>
        </div>
      </header>

      {/* CTA — Agent Faktur (analog BankAgentCta z Wyciągów).
          Renderowane bezpośrednio pod nagłówkiem — nad wyszukiwarką i listą,
          by user widział kafel bez przewijania długiej listy faktur (parity
          z innymi modułami PAX promującymi własnego agenta nad treścią). */}
      <InvoiceAgentCta hasInvoices={invoices.length > 0} />

      {/* 2026-07 v4: Wskaźnik „Zapłacone w tym miesiącu" — bezpośrednio nad
          filtrami. Widoczny gdy istnieje > 0 zapłaconych — nie zaśmieca UI
          świeżych kont. */}
      {monthlyPaid && monthlyPaid.count > 0 ? (
        <div
          className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2"
          data-testid="invoices-monthly-paid-card"
        >
          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold shrink-0">
            ✓
          </div>
          <div className="text-xs sm:text-sm text-emerald-900">
            <span className="font-semibold" data-testid="invoices-monthly-paid-label">
              Zapłacone w tym miesiącu:
            </span>{" "}
            <span
              className="font-bold tabular-nums"
              data-testid="invoices-monthly-paid-amount"
            >
              {new Intl.NumberFormat("pl-PL", {
                style: "currency",
                currency: monthlyPaid.currency || "PLN",
                maximumFractionDigits: 2,
              }).format(monthlyPaid.total_amount || 0)}
            </span>{" "}
            <span className="text-emerald-700" data-testid="invoices-monthly-paid-count">
              ({monthlyPaid.count} {monthlyPaid.count === 1 ? "faktura" : monthlyPaid.count < 5 ? "faktury" : "faktur"})
            </span>
          </div>
        </div>
      ) : null}

      {/* 2026-07 v4: Widok listy — toggle Do zapłaty / Zapłacone / Wszystkie. */}
      <div
        className="mb-3 inline-flex items-center rounded-lg border border-gray-200 bg-white p-0.5"
        data-testid="invoices-view-toggle"
      >
        {[
          { value: "to_pay", label: "Do zapłaty" },
          { value: "paid", label: "Zapłacone" },
          { value: "all", label: "Wszystkie" },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setView(opt.value)}
            className={
              "px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors " +
              (view === opt.value
                ? "bg-indigo-600 text-white font-semibold"
                : "text-gray-600 hover:bg-gray-100")
            }
            data-testid={`invoices-view-${opt.value}`}
            aria-pressed={view === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Filters - Mobile optimized: search + date range */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Input
            placeholder="Szukaj po numerze, nazwie lub NIP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input-mobile"
            data-testid="invoice-search"
          />
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>
        <DateRangeFilter
          value={dateRange}
          onApply={(range) => setDateRange(range)}
        />
        <Select value={sortValue} onValueChange={setSortValue}>
          <SelectTrigger
            className="w-full sm:w-[210px] h-10"
            data-testid="invoices-sort-select"
            aria-label="Sortuj faktury"
          >
            <SelectValue placeholder="Sortuj" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                data-testid={`invoices-sort-option-${opt.value}`}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card View */}
      <div className="cards-mobile">
        <Card className="border border-gray-200 overflow-hidden">
          {invoices.length === 0 ? (
            <div className="p-8 text-center">
              {search ? (
                <p className="text-gray-500">Brak wyników</p>
              ) : (
                <div className="space-y-3">
                  <Inbox className="w-12 h-12 text-gray-300 mx-auto" />
                  <p className="text-gray-600 font-medium">Brak zaimportowanych faktur</p>
                  <p className="text-gray-500 text-sm">
                    Przejdź do <Link to="/ksef-inbox" className="text-blue-600 hover:underline font-semibold">KSeF Inbox</Link> aby pobrać dokumenty z Krajowego Systemu e-Faktur i wybrać które zaimportować.
                  </p>
                </div>
              )}
            </div>
          ) : (
            invoices.map((invoice) => {
              // 2026-02: `payment_status` — kanoniczny enum z backendu.
              const isPaid = invoice.payment_status
                ? invoice.payment_status !== "unpaid"
                : String(invoice.status || "").toLowerCase() === "paid";
              const canExclude = invoice.status !== 'excluded' && !isPaid;
              const canAddToPackage = invoice.status !== 'in_batch' && !isPaid && invoice.status !== 'excluded';
              return (
                <div
                  key={invoice.invoice_id}
                  className={`px-3 py-3 border-b border-gray-100 transition-colors ${
                    isPaid
                      ? "bg-emerald-50/40 hover:bg-emerald-50/60"
                      : "hover:bg-blue-50/30"
                  }`}
                  data-testid={`invoice-card-${invoice.invoice_id}`}
                >
                  <div className="flex items-start gap-2">
                    {/* Lewy obszar: numer + status + sprzedawca + NIP — klikalny dla podglądu */}
                    <button
                      type="button"
                      className="flex-1 min-w-0 text-left cursor-pointer"
                      onClick={() => openPreview(invoice)}
                      data-testid={`invoice-card-body-${invoice.invoice_id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-gray-900 truncate max-w-[170px]">
                          {invoice.invoice_number}
                        </span>
                        {getStatusBadge(invoice)}
                      </div>
                      <div className="text-sm text-gray-900 truncate mt-0.5">{invoice.seller_name}</div>
                      <div className="text-xs text-gray-500 font-mono nip-nowrap">{invoice.seller_nip}</div>
                    </button>

                    {/* Prawy obszar: kwota + data + menu */}
                    <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                      <div className="font-mono text-sm font-semibold text-[#0F1F3D] whitespace-nowrap">
                        {formatAmount(invoice.gross_amount)}
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">{formatDate(invoice.due_date)}</div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 -mr-1"
                            data-testid={`invoice-menu-${invoice.invoice_id}`}
                            aria-label="Akcje faktury"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canAddToPackage && (
                            <DropdownMenuItem
                              onClick={() => handleAddToPackage(invoice)}
                              data-testid={`invoice-action-add-to-package-${invoice.invoice_id}`}
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Dodaj do paczki kontrahenta
                            </DropdownMenuItem>
                          )}
                          {/* 2026-02: Ręczne oznaczanie faktury jako zapłaconej — niezależne
                              od statusu paczki. Toggle: paid ↔ brak statusu (2026-02). */}
                          {isPaid ? (
                            <DropdownMenuItem
                              onClick={() => handleTogglePaid(invoice)}
                              data-testid={`invoice-action-unmark-paid-${invoice.invoice_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2 text-gray-500" />
                              Cofnij oznaczenie „Zapłacona”
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleTogglePaid(invoice)}
                              className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
                              data-testid={`invoice-action-mark-paid-${invoice.invoice_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Oznacz jako zapłaconą
                            </DropdownMenuItem>
                          )}
                          {canExclude && (
                            <DropdownMenuItem
                              onClick={() => handleExclude(invoice.invoice_id)}
                              data-testid={`invoice-action-exclude-${invoice.invoice_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Wyklucz
                            </DropdownMenuItem>
                          )}
                          {invoice.ksef_number && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteFromInbox(invoice)}
                              className="text-red-600"
                              data-testid={`invoice-action-delete-${invoice.invoice_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Usuń z Inboxa
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDeleteInvoice(invoice)}
                            className="text-red-600"
                            data-testid={`invoice-action-delete-from-list-${invoice.invoice_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Usuń
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </Card>
      </div>

      {/* Desktop Table View — Pakiet 1: bez overflow-x-auto; 7 kolumn 
          swobodnie mieści się w kontenerze `wide` (1536 px+). */}
      <Card className="border border-gray-200 table-desktop">
        <div>
          <table className="w-full table-responsive">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="h-10 px-4 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Nr faktury</th>
                <th className="h-10 px-4 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Sprzedawca</th>
                <th className="h-10 px-4 text-right font-medium text-gray-500 text-xs uppercase tracking-wider">Kwota brutto</th>
                <th className="h-10 px-4 text-right font-medium text-gray-500 text-xs uppercase tracking-wider">Do zapłaty</th>
                <th className="h-10 px-4 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Termin</th>
                <th className="h-10 px-4 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Status</th>
                <th className="h-10 px-4 text-right font-medium text-gray-500 text-xs uppercase tracking-wider">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    {search ? (
                      <span className="text-gray-500">Brak wyników</span>
                    ) : (
                      <div className="space-y-2">
                        <Inbox className="w-10 h-10 text-gray-300 mx-auto" />
                        <p className="text-gray-600 font-medium">Brak zaimportowanych faktur</p>
                        <p className="text-gray-500 text-sm">
                          Przejdź do <Link to="/ksef-inbox" className="text-blue-600 hover:underline font-semibold">KSeF Inbox</Link> aby pobrać dokumenty z Krajowego Systemu e-Faktur i wybrać które zaimportować.
                        </p>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  // 2026-02: `payment_status` — kanoniczny computed enum
                  // (`unpaid | paid_manual | paid_by_export`) z backendu.
                  // Fallback do `status === "paid"` gdyby backend nie zwrócił.
                  const isPaid = invoice.payment_status
                    ? invoice.payment_status !== "unpaid"
                    : String(invoice.status || "").toLowerCase() === "paid";
                  const canExclude = invoice.status !== 'excluded' && !isPaid;
              const canAddToPackage = invoice.status !== 'in_batch' && !isPaid && invoice.status !== 'excluded';
                  return (
                  <tr key={invoice.invoice_id} className={`border-b border-gray-100 transition-colors ${
                    isPaid ? "bg-emerald-50/40 hover:bg-emerald-50/60" : "hover:bg-blue-50/30"
                  }`}>
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => openPreview(invoice)}
                        className="text-left hover:underline focus:outline-none focus:underline"
                        data-testid={`invoice-desktop-number-${invoice.invoice_id}`}
                      >
                        <div className="font-mono text-sm font-medium text-blue-700">{invoice.invoice_number}</div>
                        <div className="text-xs text-gray-400">{invoice.source === 'ksef' ? 'KSeF' : invoice.source.toUpperCase()}</div>
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">{invoice.seller_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{invoice.seller_nip}</div>
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-gray-700">
                      {formatAmount(invoice.gross_amount)}
                    </td>
                    <td className="p-4 text-right font-mono text-sm font-medium text-[#0F1F3D]">
                      {formatAmount(invoice.remaining_amount)}
                    </td>
                    <td className="p-4 text-sm text-gray-700">{formatDate(invoice.due_date)}</td>
                    <td className="p-4">{getStatusBadge(invoice.status)}</td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`invoice-desktop-menu-${invoice.invoice_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canAddToPackage && (
                            <DropdownMenuItem
                              onClick={() => handleAddToPackage(invoice)}
                              data-testid={`invoice-desktop-action-add-to-package-${invoice.invoice_id}`}
                            >
                              <Package className="w-4 h-4 mr-2" />
                              Dodaj do paczki kontrahenta
                            </DropdownMenuItem>
                          )}
                          {/* 2026-02: Ręczne oznaczanie faktury jako zapłaconej (desktop). */}
                          {isPaid ? (
                            <DropdownMenuItem
                              onClick={() => handleTogglePaid(invoice)}
                              data-testid={`invoice-desktop-action-unmark-paid-${invoice.invoice_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2 text-gray-500" />
                              Cofnij oznaczenie „Zapłacona”
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleTogglePaid(invoice)}
                              className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
                              data-testid={`invoice-desktop-action-mark-paid-${invoice.invoice_id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Oznacz jako zapłaconą
                            </DropdownMenuItem>
                          )}
                          {canExclude && (
                            <DropdownMenuItem onClick={() => handleExclude(invoice.invoice_id)}>
                              <XCircle className="w-4 h-4 mr-2" />
                              Wyklucz
                            </DropdownMenuItem>
                          )}
                          {invoice.ksef_number && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteFromInbox(invoice)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Usuń z Inboxa
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDeleteInvoice(invoice)}
                            className="text-red-600"
                            data-testid={`invoice-desktop-action-delete-from-list-${invoice.invoice_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Usuń
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>


      {/* Confirm dialog: wykluczanie faktury (zamiast window.confirm — iframe-safe) */}
      <AlertDialog
        open={!!excludeDialogInvoiceId}
        onOpenChange={(open) => !open && setExcludeDialogInvoiceId(null)}
      >
        <AlertDialogContent data-testid="exclude-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Wykluczyć fakturę?</AlertDialogTitle>
            <AlertDialogDescription>
              Faktura zostanie oznaczona jako wykluczona z procesu płatności.
              Możesz ją później przywrócić zmieniając status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exclude-invoice-cancel-btn" disabled={excluding}>
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmExclude}
              disabled={excluding}
              data-testid="exclude-invoice-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {excluding ? "Wykluczam..." : "Tak, wyklucz"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm dialog: usuwanie z KSeF Inbox (ksef_invoices) */}
      <AlertDialog
        open={!!deleteDialogInvoice}
        onOpenChange={(open) => !open && setDeleteDialogInvoice(null)}
      >
        <AlertDialogContent data-testid="delete-inbox-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć fakturę z Inboxa?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialogInvoice?.ksef_number && (
                <span className="block mb-2">
                  KSeF: <span className="font-mono">{deleteDialogInvoice.ksef_number}</span>
                </span>
              )}
              Ta faktura została już zaimportowana do systemu. Usunięcie z Inboxa nie usunie jej z głównej listy &bdquo;Faktury&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-inbox-cancel-btn" disabled={deleting}>
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteFromInbox}
              disabled={deleting}
              data-testid="delete-inbox-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Usuwam..." : "Tak, usuń z Inboxa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm dialog: usuwanie faktury z modułu Faktury (kolekcja `invoices`) */}
      <AlertDialog
        open={!!deleteInvoiceDialog}
        onOpenChange={(open) => !open && setDeleteInvoiceDialog(null)}
      >
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć tę fakturę z listy Faktury?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteInvoiceDialog?.invoice_number && (
                <span className="block mb-2">
                  Nr: <span className="font-mono">{deleteInvoiceDialog.invoice_number}</span>
                </span>
              )}
              Czy na pewno chcesz usunąć tę fakturę z listy Faktury?
              {deleteInvoiceDialog?.ksef_number && (
                <span className="block mt-2 text-xs text-gray-500">
                  Faktura pozostaje w KSeF Inbox i można ją ponownie zaimportować.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-invoice-cancel-btn"
              disabled={deletingInvoice}
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteInvoice}
              disabled={deletingInvoice}
              data-testid="delete-invoice-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingInvoice ? "Usuwam..." : "Tak, usuń"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CTA Agenta Faktur przeniesione na górę (między header a filtry) —
          patrz wyżej. Tu nic nie renderujemy. */}

      {/* Round 21: podgląd faktury renderowany globalnie w
          `InvoicePreviewProvider` (MainLayout). Otwierany przez `openInvoice(inv)`. */}
    </div>
  );
};

export default InvoicesPage;
